/*
 * validations.js — vanilla JS port (no jQuery).
 *
 * Behavior preserved from the prior jQuery version, including all earlier fixes:
 *   1. `$.inArray` inversion fix kept (sanitization correctly skips password/file).
 *   2. `validateFileUpload` always returns a boolean.
 *   3. No duplicate `role`/`aria-live` attributes.
 *   4. `validateNumber` and `validatePhone` pattern-check.
 *   5. Submit handler is async (Promise-gated).
 *   6. Length-cap validators do not silently truncate input.
 *   7. Modern `setRangeText` paste insertion (no deprecated execCommand).
 *   8. `clipboard.writeText` has a `.catch` fallback.
 *   9. Phone-input handlers consolidated.
 *  10. No nested ternaries or comma-operator chains.
 */

(function () {
  'use strict';

  // ---------- DOM helpers ----------

  function qs(selector, ctx) {
    return (ctx || document).querySelector(selector);
  }

  function qsa(selector, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(selector));
  }

  function delegate(eventName, selector, handler, options) {
    document.addEventListener(eventName, function (e) {
      var match = e.target && e.target.closest ? e.target.closest(selector) : null;
      if (match) handler.call(match, e);
    }, options);
  }

  // Treat a directly-following floating <label> (input.liquid pattern) as part of
  // the anchor, so error spans don't get inserted between an input and its label.
  function getErrorAnchor(field) {
    var next = field.nextElementSibling;
    if (next && next.tagName === 'LABEL' && next.classList.contains('floating-label')) {
      return next;
    }
    return field;
  }

  function removeNextError(field, extraClass) {
    var next = getErrorAnchor(field).nextElementSibling;
    while (next && next.classList.contains('form-validation-error')) {
      var following = next.nextElementSibling;
      if (!extraClass || next.classList.contains(extraClass)) {
        next.remove();
      }
      next = following;
    }
  }

  function makeErrorSpan(message, extraClasses) {
    var span = document.createElement('span');
    span.className = 'form-validation-error' + (extraClasses ? ' ' + extraClasses : '');
    span.setAttribute('role', 'alert');
    span.setAttribute('aria-live', 'assertive');
    span.textContent = message;
    span.title = message; /* full message on hover when the line is truncated */
    return span;
  }

  function insertErrorAfter(field, span) {
    getErrorAnchor(field).after(span);
  }

  function showError(field, message, extraClasses) {
    removeNextError(field);
    insertErrorAfter(field, makeErrorSpan(message, extraClasses));
  }

  function removeError(field) {
    removeNextError(field);
  }

  function emptyCheck(field) {
    var fieldName = field.getAttribute('data-label') || '';
    var label = fieldName.replace('*', '');
    var type = field.getAttribute('type');

    if (type === 'checkbox' || type === 'radio') {
      if (!field.checked && field.classList.contains('required')) {
        showError(field, label + ' is required');
        return false;
      }
      return true;
    }

    var isMultiSelect = field.localName === 'select' && field.hasAttribute('multiple');
    var value;
    if (isMultiSelect) {
      value = Array.prototype.slice.call(field.selectedOptions).map(function (o) { return o.value; });
    } else {
      value = field.value;
    }
    var isEmpty = (isMultiSelect && (!value || value.length === 0)) || value === '' || value === null;

    if (isEmpty && field.classList.contains('required')) {
      showError(field, label + ' is required');
      return false;
    }
    return true;
  }

  // ---------- validators (synchronous) ----------

  function validateEmail(field) {
    if (!emptyCheck(field)) return false;

    var email = field.value;
    var emailParts = email.split('@');
    var EXAMPLE = ' (e.g. you@example.com)';

    if (emailParts.length !== 2) {
      showError(field, 'Email must contain a single "@"' + EXAMPLE);
      return false;
    }

    var localPart = emailParts[0];
    var domainPart = emailParts[1];
    var localRegex = /^[a-zA-Z0-9!#$%&'*+\-/=?^_`{|}~]+(\.[a-zA-Z0-9!#$%&'*+\-/=?^_`{|}~]+)*$/;

    if (localPart.length === 0) {
      showError(field, 'Add the part before "@"' + EXAMPLE);
      return false;
    }
    if (localPart.length > 64) {
      showError(field, 'The part before "@" is too long (max 64 characters)');
      return false;
    }
    if (localPart.startsWith('.') || localPart.endsWith('.') || localPart.includes('..')) {
      showError(field, 'The part before "@" cannot start, end, or contain consecutive dots' + EXAMPLE);
      return false;
    }
    if (!localRegex.test(localPart)) {
      showError(field, 'The part before "@" contains invalid characters' + EXAMPLE);
      return false;
    }

    if (domainPart.length === 0) {
      showError(field, 'Add a domain after "@"' + EXAMPLE);
      return false;
    }
    if (domainPart.length > 255) {
      showError(field, 'Domain is too long (max 255 characters)');
      return false;
    }
    if (domainPart.startsWith('-') || domainPart.endsWith('-') || domainPart.includes('--')) {
      showError(field, 'Domain cannot start, end, or contain consecutive hyphens' + EXAMPLE);
      return false;
    }

    var tldRegex = /^[a-zA-Z]{2,}$/;
    var domainNameRegex = /^[a-zA-Z0-9]+([-][a-zA-Z0-9]+)*$/;
    var domainParts = domainPart.split('.');

    if (domainParts.length < 2) {
      showError(field, 'Domain must include a top-level part like ".com"' + EXAMPLE);
      return false;
    }
    if (!tldRegex.test(domainParts[domainParts.length - 1])) {
      showError(field, 'Top-level domain must be at least 2 letters (e.g. .com, .co)');
      return false;
    }
    for (var i = 0; i < domainParts.length - 1; i++) {
      if (!domainNameRegex.test(domainParts[i])) {
        showError(field, 'Domain contains invalid characters' + EXAMPLE);
        return false;
      }
    }

    removeError(field);
    return true;
  }

  function validateText(field) {
    var value = field.value;
    if (value === '') return emptyCheck(field);

    if (field.classList.contains('min-3-characters') && value.length < 3) {
      showError(field, 'Please enter at least 3 characters');
      return false;
    }
    if (field.classList.contains('max-56-characters') && value.length > 56) {
      showError(field, 'Maximum of 56 characters allowed');
      return false;
    }
    if (field.classList.contains('max-100-characters') && value.length > 100) {
      showError(field, 'Maximum of 100 characters allowed');
      return false;
    }
    if (field.classList.contains('max-255-characters') && value.length > 255) {
      showError(field, 'Maximum of 255 characters allowed');
      return false;
    }
    removeError(field);
    return true;
  }

  function validateWebsite(field) {
    if (!emptyCheck(field)) return false;

    var website = field.value;
    if (website.length > 255) {
      showError(field, 'Website URL must be at most 255 characters');
      return false;
    }
    var urlRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/\S*)?$/;
    if (!urlRegex.test(website)) {
      showError(field, 'Please enter a valid URL');
      return false;
    }
    removeError(field);
    return true;
  }

  function validateSelect(field) {
    if (field.value === '---') field.value = '';
    return emptyCheck(field);
  }

  // ----------------------------------------------------------------------
  // validatePhone — configuration via attributes on the input:
  //
  //   Attributes:
  //     minlength="N"   Minimum total length of the value (default: 6).
  //                     Counts characters; the optional leading "+" is
  //                     included in the count.
  //     maxlength="N"   Maximum total length (default: 15). The browser
  //                     also caps typing at this length when set.
  //
  //   Allowed characters: digits 0–9 and an optional leading "+".
  //   The keystroke + paste/input handlers strip everything else and
  //   surface a transient `phone-invalid-char-error` toast explaining why.
  //
  //   Submit-time spam check (all-zeros / repeated-digit numbers) lives in
  //   the form `submit` listener inside init() and uses class
  //   `zero-phone-error`.
  // ----------------------------------------------------------------------

  function readPhoneConfig(field) {
    var minAttr = field.getAttribute('minlength');
    var maxAttr = field.getAttribute('maxlength');
    var min = (minAttr !== null && minAttr !== '') ? parseInt(minAttr, 10) : 6;
    var max = (maxAttr !== null && maxAttr !== '') ? parseInt(maxAttr, 10) : 15;
    if (isNaN(min)) min = 6;
    if (isNaN(max)) max = 15;
    return { min: min, max: max };
  }

  function validatePhone(field) {
    if (!emptyCheck(field)) return false;

    var phone = field.value;
    if (phone === '') return true;

    var cfg = readPhoneConfig(field);

    if (!/^\+?\d+$/.test(phone)) {
      showError(field, 'Phone number must contain only digits, with an optional leading "+"');
      return false;
    }
    if (phone.length < cfg.min) {
      showError(field, 'Phone number is too short (minimum ' + cfg.min + ' characters)');
      return false;
    }
    if (phone.length > cfg.max) {
      showError(field, 'Phone number is too long (maximum ' + cfg.max + ' characters)');
      return false;
    }

    removeError(field);
    return true;
  }

  function validateFax(field) {
    if (!emptyCheck(field)) return false;

    var fax = field.value;
    var faxRegex = /^(\+?\d{1,4})?[\s-]?(\(?\d{2,4}\)?)?[\s-]?\d{3,4}[\s-]?\d{4}$/;

    if (fax.length < 7 || fax.length > 20 || !faxRegex.test(fax)) {
      showError(field, 'Please enter a valid fax number');
      return false;
    }
    removeError(field);
    return true;
  }

  // ----------------------------------------------------------------------
  // validateNumber — configuration via attributes/classes on the input:
  //
  //   Attributes:
  //     min="N"               Minimum allowed value (inclusive).
  //                           Set min="0" (or any non-negative N) to forbid negatives.
  //     max="N"               Maximum allowed value (inclusive).
  //
  //   Classes:
  //     .allow-decimals       Permit "." in keystrokes and the parsed value.
  //                           Default: integers only.
  //     .no-zero              Reject the exact value 0 (regardless of min/max).
  //
  //   Examples:
  //     <input type="number" min="1" max="99">                1..99 integers only
  //     <input type="number" class="allow-decimals">          any decimal number
  //     <input type="number" min="0" class="no-zero">         positive integers (no zero)
  //     <input type="number" min="-10" max="10" class="allow-decimals">  -10..10 decimals
  // ----------------------------------------------------------------------

  function numberRangeText(min, max) {
    if (min !== null && max !== null) return 'Enter a value between ' + min + ' and ' + max;
    if (min !== null) return 'Enter a value of ' + min + ' or more';
    if (max !== null) return 'Enter a value of ' + max + ' or less';
    return 'Enter a valid number';
  }

  function numberRangeSuffix(min, max) {
    if (min !== null && max !== null) return ' (allowed: ' + min + '–' + max + ')';
    if (min !== null) return ' (minimum: ' + min + ')';
    if (max !== null) return ' (maximum: ' + max + ')';
    return '';
  }

  function readNumberConfig(field) {
    var minAttr = field.getAttribute('min');
    var maxAttr = field.getAttribute('max');
    var min = (minAttr !== null && minAttr !== '') ? parseFloat(minAttr) : null;
    var max = (maxAttr !== null && maxAttr !== '') ? parseFloat(maxAttr) : null;
    return {
      min: isNaN(min) ? null : min,
      max: isNaN(max) ? null : max,
      allowDecimals: field.classList.contains('allow-decimals'),
      noZero: field.classList.contains('no-zero'),
      allowNegative: !((minAttr !== null && minAttr !== '' && parseFloat(minAttr) >= 0))
    };
  }

  function validateNumber(field) {
    if (!emptyCheck(field)) return false;

    var value = field.value;
    if (value === '') return true;

    var cfg = readNumberConfig(field);

    var formatRegex = cfg.allowDecimals ? /^-?\d+(\.\d+)?$/ : /^-?\d+$/;
    if (!formatRegex.test(value)) {
      showError(field, cfg.allowDecimals ? 'Enter a valid number (e.g. 12.5)' : 'Enter a whole number (e.g. 12)');
      return false;
    }

    var num = parseFloat(value);

    if (!cfg.allowNegative && num < 0) {
      showError(field, 'Negative values are not allowed' + numberRangeSuffix(cfg.min, cfg.max));
      return false;
    }

    if (cfg.noZero && num === 0) {
      showError(field, 'Zero is not allowed' + numberRangeSuffix(cfg.min, cfg.max));
      return false;
    }

    if (cfg.min !== null && num < cfg.min) {
      showError(field, numberRangeText(cfg.min, cfg.max));
      return false;
    }
    if (cfg.max !== null && num > cfg.max) {
      showError(field, numberRangeText(cfg.min, cfg.max));
      return false;
    }

    removeError(field);
    return true;
  }

  function validateCheckbox(field) {
    return emptyCheck(field);
  }

  function validateFileUpload(field) {
    if (!emptyCheck(field)) return false;

    if (field.files.length === 0) return true;

    var fileName = field.files[0].name;
    var wrap = field.closest('.upload-wrapp');
    var uploadText = wrap ? wrap.querySelector('.upload-text') : null;

    if (!fileName) return true;

    if (uploadText) {
      uploadText.textContent = fileName;
      uploadText.classList.add('upload-text-color');
    }

    var fileSizeMB = field.files[0].size / (1024 * 1024);
    if (fileSizeMB > 2) {
      showError(field, 'The uploaded file is too large.');
      return false;
    }
    removeError(field);
    return true;
  }

  // ---------- dispatch ----------

  function validateField(field) {
    var type = field.getAttribute('type');
    var localName = field.localName;
    var name = field.getAttribute('name');
    var ok = false;

    if (type === 'email')                 ok = validateEmail(field);
    else if (type === 'password')         ok = emptyCheck(field);
    else if (type === 'text')             ok = field.classList.contains('website-validation') ? validateWebsite(field) : validateText(field);
    else if (localName === 'textarea')    ok = validateText(field);
    else if (localName === 'select')      ok = validateSelect(field);
    else if (type === 'tel')              ok = validatePhone(field);
    else if (type === 'checkbox')         ok = validateCheckbox(field);
    else if (type === 'file')             ok = validateFileUpload(field);
    else if (name === 'fax')              ok = validateFax(field);
    else if (type === 'number')           ok = validateNumber(field);
    else                                  ok = emptyCheck(field);

    if (ok) removeError(field);
    return ok;
  }

  function validateFieldAsync(field) {
    if (!validateField(field)) return Promise.resolve(false);
    return Promise.resolve(true);
  }

  // ---------- shared paste/insert helper ----------

  function insertTextAtCursor(input, text) {
    if (typeof input.setRangeText === 'function') {
      var start = input.selectionStart || 0;
      var end = input.selectionEnd || 0;
      input.setRangeText(text, start, end, 'end');
      input.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      input.value += text;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  // ---------- initialization (DOM-bound handlers) ----------

  function init() {
    // Normalize [required] → .required class and ensure data-label exists.
    // Keeps validation self-sufficient (no per-section inline scaffold required).
    qsa('#contact_form, .newsletter-section form, .footer-tailwind__newsletter-form').forEach(function (form) {
      qsa('input[required], textarea[required], select[required]', form).forEach(function (control) {
        control.classList.add('required');
        if (control.hasAttribute('data-label')) return;
        var labelText = '';
        if (control.id) {
          var labelEl = form.querySelector('label[for="' + control.id + '"]');
          if (labelEl) labelText = labelEl.textContent.trim();
        }
        if (!labelText) {
          var wrap = control.closest('label');
          if (wrap) labelText = wrap.textContent.trim();
        }
        if (!labelText) labelText = control.getAttribute('placeholder') || control.getAttribute('aria-label') || '';
        if (labelText) control.setAttribute('data-label', labelText);
      });
    });

    qsa('textarea[data-charcount]').forEach(updateCharCount);

    var formInputSelector =
      '#contact_form input, #contact_form textarea, ' +
      '.newsletter-section input, .newsletter-section textarea, ' +
      '.footer-tailwind__newsletter-form input';

    qsa(formInputSelector).forEach(function (field) {
      field.addEventListener('input', handleLiveValidate);
      field.addEventListener('keypress', handleLiveValidate);
    });

    function handleLiveValidate(e) {
      var inputValue = this.value;
      var trimmedValue = inputValue.trim();
      var type = this.getAttribute('type');
      var isTextarea = this.localName === 'textarea';

      if (!trimmedValue.replace(/\s/g, '').length) this.value = '';
      if (e.key === ' ' && trimmedValue.length === 0) {
        e.preventDefault();
        this.value = '';
      }
      // Strip leading whitespace. Textarea preserves trailing chars (user may be mid-line / on a new line).
      if (isTextarea) {
        if (/^\s/.test(inputValue)) this.value = inputValue.replace(/^\s+/, '');
      } else if (trimmedValue.length < inputValue.length && /^\s/.test(inputValue)) {
        this.value = trimmedValue;
      }

      // type === 'tel' and type === 'number' have their own dedicated
      // keypress/input handlers below.

      if (
        type === 'text' &&
        this.getAttribute('data-field') !== 'zip' &&
        e.key && e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey &&
        !/^[a-zA-Z\s]$/.test(e.key)
      ) {
        e.preventDefault();
      }

      // Collapse multi-space. Skip for textarea — `\s{2,}` would eat newlines and indentation.
      var skipSanitize = ['password', 'file'];
      if (!isTextarea && skipSanitize.indexOf(type) === -1) {
        var sanitizedValue = inputValue.replace(/\s{2,}/g, ' ');
        this.value = sanitizedValue;
      }

      if (this.classList.contains('required')) {
        if (this.value === '') {
          emptyCheck(this);
        } else {
          removeError(this);
          validateField(this);
        }
      } else if (this.value !== '') {
        validateField(this);
      }
    }

    qsa('#contact_form, .newsletter-section form, .footer-tailwind__newsletter-form').forEach(function (form) {
      // Capture phase: fire before any other submit listener (e.g. Shopify
      // recaptchacompat shim or hCaptcha) so our preventDefault sticks.
      form.addEventListener('submit', function (e) {
        var firstInvalid = null;

        var fields = qsa(
          'input.required:not([type="hidden"]):not([type="submit"]), textarea.required, select.required',
          form
        );
        fields.forEach(function (field) {
          if (!validateField(field) && !firstInvalid) firstInvalid = field;
        });

        qsa('input[type="tel"].required', form).forEach(function (field) {
          if (isSpamPhoneNumber(field.value)) {
            removeNextError(field);
            insertErrorAfter(field, makeErrorSpan('Phone number cannot be all zeros or repeated digits', 'zero-phone-error'));
            if (!firstInvalid) firstInvalid = field;
          }
        });

        if (!form.classList.contains('cwd__form')) {
          var body = form.querySelector('textarea[name="contact[body]"]');
          if (body && body.value) {
            var htmlTagRegex = /<\/?[^>]+(>|$)/;
            var restrictedSymbolRegex = /[{}\[\]<>|^]/;
            if (htmlTagRegex.test(body.value) || restrictedSymbolRegex.test(body.value)) {
              removeNextError(body);
              insertErrorAfter(body, makeErrorSpan('Message contains invalid characters', 'html-tag-error'));
              if (!firstInvalid) firstInvalid = body;
            }
          }
        }

        if (firstInvalid) {
          e.preventDefault();
          // stopImmediatePropagation prevents any later submit listener (e.g.
          // a Shopify shim) from running and re-triggering submission via
          // form.submit() — which would bypass preventDefault.
          e.stopImmediatePropagation();
          firstInvalid.focus();
        }
      }, true);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ---------- focusout / change (delegated, picks up dynamically added fields) ----------

  delegate('focusout', "input:not([type='file']).required, select.required, textarea.required", function () {
    emptyCheck(this);
  });

  delegate('change', 'select', function () {
    if (this.value !== '') removeError(this);
  });

  // ---------- email keystroke + paste sanitization ----------

  function showTransientError(field, message, errorClass) {
    removeNextError(field);
    insertErrorAfter(field, makeErrorSpan(message, errorClass));
    setTimeout(function () {
      removeNextError(field, errorClass);
    }, 3000);
  }

  delegate('keypress', 'input[type="email"]', function (e) {
    if (e.ctrlKey || e.metaKey || !e.key || e.key.length !== 1) return;
    if (!/^[a-zA-Z0-9@._+-]$/.test(e.key)) {
      e.preventDefault();
      showTransientError(this, 'Special characters are not allowed in email addresses', 'email-special-char-error');
    }
  });

  delegate('input', 'input[type="email"]', function () {
    var raw = this.value;
    var sanitized = raw.replace(/[^a-zA-Z0-9@._+-]/g, '');
    if (raw === sanitized) return;
    this.value = sanitized;
    showTransientError(this, 'Special characters are not allowed in email addresses', 'email-special-char-error');
  });

  // ---------- number keystroke + paste sanitization ----------
  // Configuration is read from the input itself — see the doc block above
  // validateNumber() for the supported attributes (min/max) and classes
  // (.allow-decimals, .no-zero).

  delegate('keypress', 'input[type="number"]', function (e) {
    if (e.ctrlKey || e.metaKey || !e.key || e.key.length !== 1) return;
    var cfg = readNumberConfig(this);
    var allowed = /[0-9]/.test(e.key)
      || (cfg.allowNegative && e.key === '-')
      || (cfg.allowDecimals && e.key === '.');
    if (allowed) return;

    e.preventDefault();
    var reason;
    if (e.key === '-') reason = 'Negative values are not allowed';
    else if (e.key === '.') reason = 'Decimal numbers are not allowed';
    else reason = 'Only numeric characters are allowed';
    showTransientError(this, reason, 'number-invalid-char-error');
  });

  delegate('input', 'input[type="number"]', function () {
    var cfg = readNumberConfig(this);
    var pattern = cfg.allowDecimals
      ? (cfg.allowNegative ? /[^0-9.\-]/g : /[^0-9.]/g)
      : (cfg.allowNegative ? /[^0-9\-]/g : /[^0-9]/g);
    var raw = this.value;
    var sanitized = raw.replace(pattern, '');
    if (raw === sanitized) return;
    this.value = sanitized;
    showTransientError(this, 'Some characters were not allowed and have been removed', 'number-invalid-char-error');
  });

  // ---------- phone spam check + paste sanitization ----------

  function isSpamPhoneNumber(phone) {
    var digits = phone.replace(/\D/g, '');
    return /^0+$/.test(digits) || /^(\d)\1+$/.test(digits);
  }

  // Keep a single leading "+" if present; strip everything else non-digit.
  function sanitizePhoneValue(raw) {
    var leadingPlus = raw.charAt(0) === '+' ? '+' : '';
    return leadingPlus + raw.replace(/\D/g, '');
  }

  delegate('keypress', 'input[type="tel"]', function (e) {
    if (e.ctrlKey || e.metaKey || !e.key || e.key.length !== 1) return;
    if (/[0-9]/.test(e.key)) return;
    if (e.key === '+') {
      var pos = this.selectionStart || 0;
      if (pos === 0 && this.value.charAt(0) !== '+') return;
      e.preventDefault();
      showTransientError(this, 'The "+" sign is only allowed at the start of the number', 'phone-invalid-char-error');
      return;
    }
    e.preventDefault();
    showTransientError(this, 'Only digits and an optional leading "+" are allowed', 'phone-invalid-char-error');
  });

  delegate('paste', 'input[type="tel"]', function (e) {
    e.preventDefault();
    var clipboard = e.clipboardData || window.clipboardData;
    var raw = clipboard ? (clipboard.getData('text') || '') : '';
    var sanitized = sanitizePhoneValue(raw);
    insertTextAtCursor(this, sanitized);
    if (raw !== sanitized) {
      showTransientError(this, 'Some characters from your paste were removed (only digits and a leading "+" are allowed)', 'phone-invalid-char-error');
    }
  });

  delegate('input', 'input[type="tel"]', function () {
    var raw = this.value;
    var sanitized = sanitizePhoneValue(raw);
    if (raw !== sanitized) {
      this.value = sanitized;
      showTransientError(this, 'Some characters were not allowed and have been removed', 'phone-invalid-char-error');
    }
    if (!isSpamPhoneNumber(this.value)) {
      removeNextError(this, 'zero-phone-error');
    }
  });

  // (Phone-spam check at submit time is handled inside the form `submit` listener in init().)

  // ---------- contact_form message: block HTML / restricted symbols ----------

  (function () {
    var htmlTagRegex = /<\/?[^>]+(>|$)/;
    var restrictedSymbolRegex = /[{}\[\]<>|^]/;
    var messageSelector = '#contact_form textarea[name="contact[body]"]';

    function showHtmlError(field) {
      removeNextError(field);
      insertErrorAfter(field, makeErrorSpan('Message contains invalid characters', 'html-tag-error'));
    }

    function removeHtmlError(field) {
      removeNextError(field, 'html-tag-error');
    }

    function checkAndShow(field) {
      if (field.closest('.cwd__form')) return;
      var value = field.value;
      if (value && (htmlTagRegex.test(value) || restrictedSymbolRegex.test(value))) {
        showHtmlError(field);
      } else {
        removeHtmlError(field);
      }
    }

    delegate('input', messageSelector, function () { checkAndShow(this); });
    delegate('paste', messageSelector, function () {
      var self = this;
      setTimeout(function () { checkAndShow(self); }, 0);
    });

    // (Submit-time HTML/symbol check is handled inside the form `submit` listener in init().)
  })();

  // ---------- textarea max-1200 ----------

  delegate('input', 'textarea.max-1200-characters', function () {
    var length = this.value.length;
    removeNextError(this);

    if (length > 1200) {
      insertErrorAfter(this, makeErrorSpan('Maximum of 1200 characters allowed'));
      return;
    }
    if (length > 0 && length < 3) {
      insertErrorAfter(this, makeErrorSpan('Please enter at least 3 characters'));
    }
  });

  // ---------- textarea live character counter ----------
  // <textarea maxlength="1000" data-charcount> inside a [data-charcount-field]
  // container that also holds a [data-charcount-display] element → shows "N / max".
  function updateCharCount(ta) {
    var max = parseInt(ta.getAttribute('maxlength'), 10);
    if (!max) return;
    var field = ta.closest('[data-charcount-field]') || ta.parentElement;
    var disp = field && field.querySelector('[data-charcount-display]');
    if (disp) disp.textContent = ta.value.length + ' / ' + max;
  }
  delegate('input', 'textarea[data-charcount]', function () { updateCharCount(this); });

  // ---------- cart confirmation prompts ----------

  document.addEventListener('click', function (e) {
    var minus = e.target.closest('.quantity-selector__button');
    if (!minus) return;
    var selector = minus.closest('quantity-selector');
    if (!selector) return;
    if (selector.firstElementChild !== minus) return;
    var input = selector.querySelector('.quantity-selector__input');
    if (input && parseInt(input.value, 10) <= 1) {
      if (!confirm('Are you sure you want to remove this item from cart?')) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return false;
      }
    }
  }, true);

  // ---------- cart:error rewrite for product-add forms ----------
  //
  // Shopify's `/cart/add.js` returns a message like "Only N items were added
  // to your cart due to availability" where N is the inventory total, not the
  // actually-addable amount (= inventory − cart_qty_for_variant). The PDP /
  // quick-buy / featured-product quantity inputs all have their `max`
  // attribute server-rendered with the correct addable value (see
  // snippets/quantity-selector.liquid:41-46), so we swap that number into the
  // message synchronously in capture phase, before the banner code in
  // theme.js reads `event.detail.error`.
  //
  // Covered forms (by id prefix):
  //   product-form-main-…            (PDP)
  //   product-form-quick-buy-…       (quick-buy modal)
  //   product-form-featured-product-…(featured product section)

  // Cart cache keyed by variant id, primed once from /cart.js and refreshed
  // off every cart:change event. The `max` attribute on the qty input can't
  // be trusted on its own because the quick-buy modal fetches the product
  // page through `cachedFetch` (theme.js cachedFetch Map) which never
  // invalidates on cart changes — so opening quick-buy after a prior
  // successful add returns cached HTML with a stale, pre-add `max`. The raw
  // `data-variant-inventory` value isn't subject to that staleness, so we
  // recompute addable = inventory − inCart whenever we have a primed cache.
  //
  // The quantity input lives outside the (hidden) <form> element and is
  // form-associated via `form="…"` (see snippets/product-info.liquid), so we
  // walk from the inputs and resolve their owning form via `input.form` —
  // `form.querySelector` only walks descendants and would miss them.
  var cartByVariant = Object.create(null);
  var cartByVariantPrimed = false;

  function applyCartItemsToCache(items) {
    if (!Array.isArray(items)) return;
    cartByVariant = Object.create(null);
    items.forEach(function (item) {
      var vid = String(item.variant_id);
      cartByVariant[vid] = (cartByVariant[vid] || 0) + item.quantity;
    });
    cartByVariantPrimed = true;
    qsa('input[name="quantity"][data-variant-inventory]').forEach(refreshMaxAttribute);
  }

  // Prime once at module init so a fresh page load with a non-empty cart
  // (or a quick-buy reopen after a cart:change in this session) computes
  // the correct addable count on the very first overflow attempt.
  fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart.js', {
    credentials: 'same-origin',
    headers: { 'Accept': 'application/json' }
  })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (cart) { if (cart) applyCartItemsToCache(cart.items); })
    .catch(function () { /* offline / blocked — fall through to `max` attribute */ });

  function findFormQtyInput(form) {
    var candidates = document.querySelectorAll('input[name="quantity"]');
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i].form === form) return candidates[i];
    }
    return null;
  }

  // One-shot stylesheet for the "fully in cart" state. We can't set `disabled`
  // on Shopify's dynamic Buy It Now (.shopify-payment-button is third-party
  // DOM), so we dim + pointer-events:none it via a class on the form. Submit
  // buttons we own get a real `disabled` attribute in code, so :disabled
  // styling from the theme covers them.
  (function () {
    if (document.getElementById('product-form-cap-style')) return;
    var style = document.createElement('style');
    style.id = 'product-form-cap-style';
    style.textContent =
      '.product-form-cap-message{color:rgb(var(--color-error, 192 0 0));margin-top:.5rem;}' +
      'form.product-form--cap .shopify-payment-button{opacity:.5;pointer-events:none;}' +
      'form.product-form--cap button[type="submit"],form.product-form--cap input[type="submit"]{opacity:.5;}';
    document.head.appendChild(style);
  })();

  var FULLY_IN_CART_MESSAGE = 'All available units are already in your cart';

  function refreshMaxAttribute(qty) {
    var form = qty.form;
    // Use getAttribute('id') because HTMLFormElement supports named property
    // access — when the form has an `<input name="id">` (the variant id
    // input on product-add forms), `form.id` resolves to that input element
    // in many browsers, not the form's HTML id string.
    if (!form || !/^product-form-/.test(form.getAttribute('id') || '')) return;
    var variantInput = form.elements && form.elements['id'];
    if (!variantInput) return;
    var inventory = parseInt(qty.getAttribute('data-variant-inventory'), 10);
    if (!isFinite(inventory)) return;
    var inCart = cartByVariant[String(variantInput.value)] || 0;
    var addable = Math.max(inventory - inCart, 0);
    qty.setAttribute('max', String(addable));
    // Only treat as "fully in cart" when the user actually has units of this
    // variant in their cart. addable === 0 with inCart === 0 means the variant
    // is sold out (inventory === 0) — buy-buttons.liquid already renders the
    // "Sold out" disabled button for that case, and the "All available units
    // are already in your cart" wording would be misleading (none are).
    applyFullyInCartUi(form, qty, addable === 0 && inCart > 0);
  }

  // When the entire stock is already in the user's cart, max=0 / min=1 /
  // value=1 leaves the input in a permanently-invalid state. The browser's
  // native rangeOverflow tooltip — "Please select a value that is no more
  // than 0" — reads as nonsense (no real "0" cap; the cause is the cart
  // already holds it all). Instead we render an inline message below the
  // quantity selector and disable the form's submit buttons, matching the
  // cart-line-cap-message pattern used in the cart drawer/page. Each piece
  // is tagged with data-cap-disabled so a later refresh (line removed from
  // cart → stock frees up) can safely undo only what we touched, without
  // re-enabling buttons that were disabled for unrelated reasons (e.g. the
  // sold_out_button assignment in buy-buttons.liquid). setCustomValidity is
  // kept as a safety net for any submit path that bypasses our `disabled`.
  function applyFullyInCartUi(form, qty, fullyInCart) {
    var wrapper = qty.closest('quantity-selector') || qty.parentElement || qty;
    var anchor = wrapper.parentElement ? wrapper : qty;
    var siblingContainer = anchor.parentElement || form;
    var existingMsg = siblingContainer.querySelector(':scope > .product-form-cap-message');

    if (fullyInCart) {
      qty.setCustomValidity(FULLY_IN_CART_MESSAGE);
      form.classList.add('product-form--cap');
      if (!existingMsg) {
        var msg = document.createElement('p');
        msg.className = 'product-form-cap-message text-xs';
        msg.setAttribute('role', 'alert');
        msg.setAttribute('aria-live', 'polite');
        msg.textContent = FULLY_IN_CART_MESSAGE;
        anchor.insertAdjacentElement('afterend', msg);
      }
      form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach(function (btn) {
        if (!btn.disabled) {
          btn.disabled = true;
          btn.setAttribute('data-cap-disabled', '');
        }
      });
    } else {
      qty.setCustomValidity('');
      form.classList.remove('product-form--cap');
      if (existingMsg) existingMsg.remove();
      form.querySelectorAll('button[data-cap-disabled], input[data-cap-disabled]').forEach(function (btn) {
        btn.removeAttribute('data-cap-disabled');
        btn.disabled = false;
      });
    }
  }

  // Listen on documentElement (not document) — cart:change is dispatched on
  // documentElement and in this theme propagation to document is suppressed
  // somewhere between (probably a `stopPropagation` in another script), so a
  // plain document-level listener never fires.
  document.documentElement.addEventListener('cart:change', function (e) {
    var cart = e.detail && e.detail.cart;
    if (cart) applyCartItemsToCache(cart.items);
  });

  // Quick-buy modal content is served from theme.js `cachedFetch` (a URL-keyed
  // Map with no cart-change invalidation — theme.js:262), so its qty input's
  // `max` attribute is whatever it was when the modal HTML was first fetched.
  // After a successful add closes the modal, the cart:change refresh above
  // skips those inputs (they're no longer in the DOM); on the next open the
  // cached HTML reinserts them with a stale, too-high `max`. The QuantityInput
  // web component's client-side over-max guard (theme.js:2615 onValueInput_fn)
  // and stepDown clamp (theme.js:2628 onValueChange_fn) both miss when max is
  // too high, so the user submits an over-cap quantity that Shopify silently
  // partial-fills (200 OK, no cart:error). Refresh max as inputs land in DOM.
  new MutationObserver(function (records) {
    var sel = 'input[name="quantity"][data-variant-inventory]';
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches(sel)) {
          if (cartByVariantPrimed) refreshMaxAttribute(node);
        } else if (node.querySelectorAll) {
          var inputs = node.querySelectorAll(sel);
          for (var k = 0; k < inputs.length; k++) {
            if (cartByVariantPrimed) refreshMaxAttribute(inputs[k]);
          }
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  document.documentElement.addEventListener('cart:error', function (e) {
    var msg = e.detail && e.detail.error;
    if (typeof msg !== 'string') return;
    if (!/due to availability/i.test(msg)) return;
    var form = e.target;
    // Same form.id-vs-named-control caveat as in refreshMaxAttribute — read
    // the id attribute via getAttribute so we get a string, not the named
    // <input name="id"> element.
    if (!form || form.tagName !== 'FORM' || !/^product-form-/.test(form.getAttribute('id') || '')) return;
    var qty = findFormQtyInput(form);
    if (!qty) return;

    // Prefer the data-variant-inventory + cartByVariant math when the cache
    // has been primed — the `max` attribute can be stale on quick-buy modal
    // reopens because the modal's HTML is served from cachedFetch. Fall
    // back to `max` only if the prime fetch failed or data-variant-inventory
    // isn't present (e.g., untracked-inventory variant).
    var variantInput = form.elements && form.elements['id'];
    var inventory = parseInt(qty.getAttribute('data-variant-inventory'), 10);
    var addable;
    if (cartByVariantPrimed && isFinite(inventory) && variantInput) {
      var inCart = cartByVariant[String(variantInput.value)] || 0;
      addable = Math.max(inventory - inCart, 0);
    } else {
      addable = parseInt(qty.getAttribute('max'), 10);
    }
    if (!isFinite(addable) || addable < 0) return;
    e.detail.error = msg.replace(/\b\d+\b/, addable);
  }, true);

  // ---------- Cart line item: surface silent quantity capping ----------
  //
  // /cart/change.js silently caps the new quantity to the available stock
  // and returns 200, so the user sees their input snap to a lower value
  // with no explanation (the change handler in theme.js only inserts an
  // alert on a non-OK response). We pair the theme's own lifecycle events:
  //   line-item:will-change  →  detail.targetQuantity (what the user asked for)
  //   line-item:change       →  detail.quantity       (what Shopify actually applied)
  // If actual < target, the line was capped — show a transient inline note.
  //
  // (On the cart *page*, theme.js calls window.location.reload() in the
  // change handler instead of dispatching line-item:change, so this only
  // fires in the cart drawer / non-cart-page contexts.)

  (function () {
    var pendingTargets = new WeakMap();

    document.addEventListener('line-item:will-change', function (e) {
      var lineItem = e.target;
      if (!lineItem || !e.detail || typeof e.detail.targetQuantity !== 'number') return;
      pendingTargets.set(lineItem, e.detail.targetQuantity);
    });

    document.addEventListener('line-item:change', function (e) {
      var lineItem = e.target;
      if (!lineItem) return;
      var target = pendingTargets.get(lineItem);
      pendingTargets.delete(lineItem);
      if (typeof target !== 'number') return;
      var actual = e.detail && e.detail.quantity;
      if (typeof actual !== 'number' || actual >= target) return;

      var anchor = lineItem.querySelector('line-item-quantity') || lineItem;
      var siblingContainer = anchor.parentElement || lineItem;
      var existing = siblingContainer.querySelector('.cart-line-cap-message');
      if (existing) existing.remove();

      var msg = document.createElement('p');
      msg.className = 'cart-line-cap-message text-xs';
      msg.setAttribute('role', 'alert');
      msg.setAttribute('aria-live', 'polite');
      msg.textContent = 'Only ' + actual + ' items are available';
      anchor.insertAdjacentElement('afterend', msg);
      setTimeout(function () { if (msg.parentElement) msg.remove(); }, 4000);
    });
  })();


  // ---------- product share button ----------

  // function bindShareButton() {
  //   var shareBtn = document.querySelector('.product-share-btn');
  //   if (!shareBtn) return;

  //   shareBtn.addEventListener('click', function () {
  //     var productUrl = window.location.href;
  //     var productTitle = document.title;

  //     if (navigator.share) {
  //       navigator.share({ title: productTitle, text: 'Check out this product', url: productUrl })
  //         .catch(function (err) { console.log('Share cancelled', err); });
  //       return;
  //     }
  //     if (navigator.clipboard && navigator.clipboard.writeText) {
  //       navigator.clipboard.writeText(productUrl)
  //         .then(function () { alert('Product link copied!'); })
  //         .catch(function () { alert('Could not copy link. URL: ' + productUrl); });
  //       return;
  //     }
  //     alert('Product link: ' + productUrl);
  //   });
  // }

  // if (document.readyState === 'loading') {
  //   document.addEventListener('DOMContentLoaded', bindShareButton);
  // } else {
  //   bindShareButton();
  // }

  // ---------- Name fields: letters / letters+space rules ----------
  //
  //   Full Name  (contact[name])                              letters + single space, no leading space, trim on blur
  //   First Name (customer[first_name], address[first_name])  letters only; min 2, max 56 (via HTML attrs in the Liquid render)
  //   Last Name  (customer[last_name], address[last_name])    letters only; max 56 (via HTML attrs in the Liquid render)

  var fullNameSelector = 'input[name="contact[name]"]';
  var firstLastNameSelector =
    'input[name="customer[first_name]"], input[name="address[first_name]"], ' +
    'input[name="customer[last_name]"], input[name="address[last_name]"]';
  var nameFieldSelector = fullNameSelector + ', ' + firstLastNameSelector;

  function isFullNameField(field) {
    return field.matches(fullNameSelector);
  }

  function cleanNameValue(field, raw) {
    if (isFullNameField(field)) {
      return raw.replace(/[^a-zA-Z ]/g, '').replace(/ {2,}/g, ' ').replace(/^ /, '');
    }
    return raw.replace(/[^a-zA-Z]/g, '');
  }

  delegate('keypress', nameFieldSelector, function (e) {
    if (e.ctrlKey || e.metaKey || !e.key || e.key.length > 1) return;
    if (/^[a-zA-Z]$/.test(e.key)) return;
    if (e.key === ' ' && isFullNameField(this)) {
      var pos = this.selectionStart != null ? this.selectionStart : this.value.length;
      if (pos === 0 || this.value.charAt(pos - 1) === ' ') e.preventDefault();
      return;
    }
    e.preventDefault();
  });

  delegate('paste', nameFieldSelector, function (e) {
    e.preventDefault();
    var clipboard = e.clipboardData || window.clipboardData;
    var raw = clipboard ? (clipboard.getData('text') || '') : '';
    var cleaned = cleanNameValue(this, raw);
    if (isFullNameField(this)) cleaned = cleaned.replace(/^\s+|\s+$/g, '');
    insertTextAtCursor(this, cleaned);
  });

  delegate('input', nameFieldSelector, function () {
    var cleaned = cleanNameValue(this, this.value);
    if (this.value === cleaned) return;
    var pos = this.selectionStart;
    this.value = cleaned;
    if (typeof pos === 'number') {
      try { this.selectionStart = this.selectionEnd = Math.min(pos, cleaned.length); } catch (_) {}
    }
  });

  // Full Name only: trim trailing whitespace on blur (leading is already blocked live).
  delegate('focusout', fullNameSelector, function () {
    var trimmed = this.value.replace(/\s+$/g, '');
    if (this.value !== trimmed) this.value = trimmed;
  });

  // Touch-tap tooltip for disabled [data-tooltip] elements (e.g. cart "+"
  // button at max stock). Desktop hover is handled by CSS and stays untouched;
  // this only adds a tap-to-toggle path because :hover doesn't fire on touch.
  // pointerdown + capture is required because disabled buttons never fire click.
  (function () {
    var openEl = null;
    var hideTimer = null;

    function close() {
      if (openEl) {
        openEl.classList.remove('is-tooltip-open');
        openEl = null;
      }
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
    }

    document.addEventListener('pointerdown', function (e) {
      var path = (typeof e.composedPath === 'function') ? e.composedPath() : [];
      var hit = null;
      for (var i = 0; i < path.length; i++) {
        var el = path[i];
        if (el && el.nodeType === 1 && el.hasAttribute &&
            el.hasAttribute('data-tooltip') && el.hasAttribute('disabled')) {
          hit = el;
          break;
        }
      }
      if (!hit) { close(); return; }
      if (openEl === hit) { close(); return; }
      close();
      hit.classList.add('is-tooltip-open');
      openEl = hit;
      hideTimer = setTimeout(close, 2500);
    }, true);
  })();

})();
